import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  List,
  Popconfirm,
  DatePicker,
  Modal,
  Checkbox,
  message,
  Input
} from 'antd';
import {
  EyeOutlined,
  WhatsAppOutlined,
  ClockCircleOutlined,
  PercentageOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  StopOutlined,
  DeleteOutlined,
  SearchOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import locale from 'antd/es/date-picker/locale/pt_BR';
import dayjs from 'dayjs';
import { GET_CAMPAIGN_DETAILS, GET_CAMPAIGN_MESSAGES } from '../../graphql/queries/campaign';
import { GET_ACCOUNT } from '../../mutations/account';
import { SCHEDULE_CAMPAIGN } from '../../graphql/mutations/scheduleCampaign';
import { CANCEL_CAMPAIGN_MESSAGE } from '../../graphql/mutations/cancelCampaignMessage';
import { CANCEL_ALL_CAMPAIGN_MESSAGES } from '../../graphql/mutations/cancelAllCampaignMessages';
import { DELETE_CAMPAIGN_MESSAGE } from '../../graphql/mutations/deleteCampaignMessage';
import { DELETE_SELECTED_CAMPAIGN_MESSAGES } from '../../graphql/mutations/deleteSelectedCampaignMessages';
import CampaignMessageModal from './CampaignMessageModal';
import { getStatusColor, getStatusLabel, getKindIcon, getKindLabel } from '../../utils/campaignUtils';
import { isCurrentTimeValid } from '../../utils/campaignHoursUtils';

const { Title } = Typography;



const CampaignDetailsModal = ({ campaignId, onClose }) => {
  const [activeTab, setActiveTab] = useState('1');
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesPerPage, setMessagesPerPage] = useState(10);
  const [messageStatus, setMessageStatus] = useState("");
  const [messageModalVisible, setMessageModalVisible] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [hasScheduledMessages, setHasScheduledMessages] = useState(false);
  const [rescheduleModalVisible, setRescheduleModalVisible] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isCurrentValid, setIsCurrentValid] = useState(true);
  
  // Estados para seleção de mensagens
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Estados para pesquisa
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Create a dedicated function to load messages with explicit error handling
  const loadMessages = async (status = "", page = 1) => {
    console.log(`🔄 Loading messages: status=${status}, page=${page}, campaignId=${campaignId}`);
    try {
      const result = await refetchMessages({
        campaignId: campaignId,
        page: page,
        perPage: messagesPerPage,
        status: status || null
      });
      
      console.log(`✅ Loaded ${result?.data?.campaignMessages?.totalCount || 0} messages`);
      
      // Check for scheduled messages
      if (status === "" || status === "scheduled") {
        const hasScheduled = result?.data?.campaignMessages?.edges.some(
          ({ node }) => node.status === "scheduled"
        );
        setHasScheduledMessages(hasScheduled);
      }
      
      return result?.data?.campaignMessages;
    } catch (error) {
      console.error(`❌ Error loading messages:`, error);
      return null;
    }
  };

  const { loading: campaignLoading, error: campaignError, data: campaignData, refetch: refetchCampaign } = useQuery(GET_CAMPAIGN_DETAILS, {
    variables: { id: campaignId },
    fetchPolicy: 'network-only',
  });

  const { data: accountData } = useQuery(GET_ACCOUNT, {
    fetchPolicy: 'network-only'
  });

  const account = accountData?.account;

  const { loading: messagesLoading, data: messagesData, refetch: refetchMessages } = useQuery(GET_CAMPAIGN_MESSAGES, {
    variables: {
      campaignId: campaignId,
      page: messagesPage,
      perPage: messagesPerPage,
      status: messageStatus || null,
      search: searchTerm || null
    },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      // Verifica se existem mensagens agendadas apenas quando não estamos filtrando
      if (messageStatus === "" || messageStatus === "scheduled") {
        const hasScheduled = data?.campaignMessages?.edges.some(({ node }) => node.status === "scheduled");
        setHasScheduledMessages(hasScheduled);
      }
    },
  });

  // Load messages when component mounts and campaign data is loaded
  useEffect(() => {
    // Only run once when campaign data is available and not already initialized
    if (campaignData && !isInitialized) {
      console.log("Campaign data loaded, initializing messages");
      setIsInitialized(true);
      
      // Load messages after a short delay to let everything settle
      setTimeout(() => {
        loadMessages("", 1);
      }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignData, isInitialized]);

  // Update the filter function to use our dedicated function
  const handleFilterMessages = (status) => {
    console.log("Filtering messages with status:", status);
    setMessageStatus(status);
    setMessagesPage(1);
    
    // Use our dedicated function to load messages
    loadMessages(status, 1);
  };

  // Função para realizar a pesquisa
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setMessagesPage(1);
    setSelectedMessages([]); // Limpar seleções ao pesquisar
  };

  // Função para limpar a pesquisa
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setMessagesPage(1);
    setSelectedMessages([]); // Limpar seleções ao limpar pesquisa
  };

  // Função para pesquisar ao pressionar Enter
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const [scheduleCampaign, { loading: scheduling }] = useMutation(SCHEDULE_CAMPAIGN);
  const [cancelCampaignMessage, { loading: canceling }] = useMutation(CANCEL_CAMPAIGN_MESSAGE);
  const [cancelAllCampaignMessages, { loading: cancelingAll }] = useMutation(CANCEL_ALL_CAMPAIGN_MESSAGES);
  const [deleteCampaignMessage, { loading: deleting }] = useMutation(DELETE_CAMPAIGN_MESSAGE);
  const [deleteSelectedCampaignMessages, { loading: deletingSelected }] = useMutation(DELETE_SELECTED_CAMPAIGN_MESSAGES);

  useEffect(() => {
    if (account?.campaignHours) {
      setIsCurrentValid(isCurrentTimeValid(account.campaignHours));
    }
  }, [account]);

  const disabledTime = (current) => {
    if (!current) return {};

    const disabledPastTime = {};
    const now = dayjs();

    if (current.isSame(now, 'day')) {
      disabledPastTime.disabledHours = () => Array.from({ length: now.hour() }, (_, i) => i);
      disabledPastTime.disabledMinutes = (selectedHour) => (
        selectedHour === now.hour() ? Array.from({ length: now.minute() }, (_, i) => i) : []
      );
    }

    if (!account?.campaignHours) return disabledPastTime;

    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][current.day()];
    const dayConfig = account.campaignHours[dayOfWeek];

    if (!dayConfig) return { disabledHours: () => Array.from({ length: 24 }, (_, i) => i) };

    if (dayConfig.always_open) return disabledPastTime;

    if (!dayConfig.intervals || dayConfig.intervals.length === 0) {
      return { disabledHours: () => Array.from({ length: 24 }, (_, i) => i) };
    }

    const disabledHours = [];

    for (let hour = 0; hour < 24; hour++) {
      let hourIsValid = false;

      for (const interval of dayConfig.intervals) {
        const startHour = parseInt(interval.start.split(':')[0]);
        const startMinute = parseInt(interval.start.split(':')[1]);
        const endHour = parseInt(interval.end.split(':')[0]);
        const endMinute = parseInt(interval.end.split(':')[1]);

        if (hour > startHour && hour < endHour) {
          hourIsValid = true;
          break;
        } else if (hour === startHour && hour === endHour) {
          hourIsValid = true;
          break;
        } else if (hour === startHour || hour === endHour) {
          hourIsValid = true;
          break;
        }
      }

      if (!hourIsValid) {
        disabledHours.push(hour);
      }
    }

    const disabledMinutes = (selectedHour) => {
      const enabledMinutes = new Set();

      for (const interval of dayConfig.intervals) {
        const startHour = parseInt(interval.start.split(':')[0]);
        const startMinute = parseInt(interval.start.split(':')[1]);
        const endHour = parseInt(interval.end.split(':')[0]);
        const endMinute = parseInt(interval.end.split(':')[1]);

        if (selectedHour > startHour && selectedHour < endHour) {
          for (let minute = 0; minute < 60; minute++) {
            enabledMinutes.add(minute);
          }
        } else if (selectedHour === startHour && selectedHour === endHour) {
          for (let minute = startMinute; minute <= endMinute; minute++) {
            enabledMinutes.add(minute);
          }
        } else if (selectedHour === startHour) {
          for (let minute = startMinute; minute < 60; minute++) {
            enabledMinutes.add(minute);
          }
        } else if (selectedHour === endHour) {
          for (let minute = 0; minute <= endMinute; minute++) {
            enabledMinutes.add(minute);
          }
        }
      }

      const disabled = [];
      for (let minute = 0; minute < 60; minute++) {
        if (!enabledMinutes.has(minute)) {
          disabled.push(minute);
        }
      }

      return disabled;
    };

    return {
      disabledHours: () => [...new Set([...(disabledPastTime.disabledHours?.() || []), ...disabledHours])],
      disabledMinutes: (selectedHour) => [...new Set([...(disabledPastTime.disabledMinutes?.(selectedHour) || []), ...disabledMinutes(selectedHour)])]
    };
  };

  const disabledDate = (current) => {
    if (!current) return false;

    if (current.isBefore(dayjs(), 'day')) return true;

    if (!account?.campaignHours) return false;

    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][current.day()];
    const dayConfig = account.campaignHours[dayOfWeek];

    if (!dayConfig || (!dayConfig.always_open && (!dayConfig.intervals || dayConfig.intervals.length === 0))) {
      return true;
    }

    return false;
  };

  const handleReschedule = async () => {
    try {
      // Primeiro, vamos buscar as mensagens pendentes
      await refetchMessages();

      // Garantir que estamos filtrando apenas mensagens pendentes
      if (messageStatus !== "pending") {
        setMessageStatus("pending");
        await new Promise(resolve => setTimeout(resolve, 300)); // Pequeno delay para garantir que o filtro foi aplicado
        await refetchMessages();
      }

      // Verificar se existem mensagens pendentes
      const pendingMessages = messagesData?.campaignMessages?.edges || [];
      if (pendingMessages.length === 0) {
        alert("Não há mensagens pendentes para reagendar.");
        return;
      }

      // Abrir modal de agendamento
      setRescheduleModalVisible(true);
    } catch (error) {
      console.error(error);
      alert(`Erro ao preparar reagendamento: ${error.message}`);
    }
  };

  const handleConfirmReschedule = async () => {
    try {
      if (!scheduledDate && !window.confirm("Deseja realmente agendar para envio imediato?")) {
        return;
      }

      if (scheduledDate && scheduledDate.isBefore(dayjs().subtract(5, 'minute'))) {
        message.error('A data de reagendamento não pode ser anterior ao horário atual.');
        return;
      }

      // Agendar mensagens pendentes para a data selecionada ou envio imediato
      const scheduleResult = await scheduleCampaign({
        variables: {
          id: campaignId,
          scheduledFor: scheduledDate ? scheduledDate.toISOString() : null
        }
      });

      if (scheduleResult.data?.scheduleCampaign?.errors?.length > 0) {
        alert(scheduleResult.data.scheduleCampaign.errors.join('\n'));
        return;
      }

      alert(`Reagendamento realizado com sucesso! ${scheduleResult.data.scheduleCampaign.campaignMessagesCount} mensagens foram reagendadas.`);
      setRescheduleModalVisible(false);
      setScheduledDate(null);
      
      // Após reagendar, atualizar o estado hasScheduledMessages
      setHasScheduledMessages(true);
      
      // Mudar para a aba de mensagens agendadas
      setMessageStatus("scheduled");
      
      // Voltar para a primeira página
      setMessagesPage(1);
      
      // Recarregar mensagens para mostrar o novo status
      await refetchMessages();
    } catch (error) {
      console.error(error);
      alert(`Erro ao reagendar mensagens: ${error.message}`);
    }
  };

  const handleCancelAllSchedules = async () => {
    try {
      // Verificar se existem mensagens agendadas
      await refetchMessages();
      
      // Filtrar para mensagens agendadas se ainda não estiver filtrado
      if (messageStatus !== "scheduled") {
        setMessageStatus("scheduled");
        await new Promise(resolve => setTimeout(resolve, 300)); // Pequeno delay
        await refetchMessages();
      }
      
      const scheduledMessages = messagesData?.campaignMessages?.edges || [];
      if (scheduledMessages.length === 0) {
        alert("Não há mensagens agendadas para cancelar.");
        return;
      }
      
      const { data } = await cancelAllCampaignMessages({
        variables: {
          campaignId: campaignId
        }
      });
      
      if (data.cancelAllCampaignMessages.errors && data.cancelAllCampaignMessages.errors.length > 0) {
        alert(data.cancelAllCampaignMessages.errors.join('\n'));
        return;
      }
      
      alert(`Cancelamento realizado com sucesso! ${data.cancelAllCampaignMessages.count} mensagens tiveram o agendamento cancelado.`);
      setHasScheduledMessages(false);
      refetchMessages();
    } catch (error) {
      console.error('Erro ao cancelar agendamentos:', error);
      alert(`Erro ao cancelar agendamentos: ${error.message}`);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const handlePageChange = (page, pageSize) => {
    setMessagesPage(page);
    setMessagesPerPage(pageSize);
  };

  const handleViewMessage = (message) => {
    setCurrentMessage(message);
    setMessageModalVisible(true);
  };

  const handleCloseMessageModal = () => {
    setMessageModalVisible(false);
    setCurrentMessage(null);
  };

  const handleCancelSchedule = async (messageId) => {
    try {
      const { data } = await cancelCampaignMessage({
        variables: {
          id: messageId
        }
      });

      if (data.cancelCampaignMessage.errors && data.cancelCampaignMessage.errors.length > 0) {
        alert(data.cancelCampaignMessage.errors.join('\n'));
        return;
      }

      // Refetch messages to update the UI
      refetchMessages();
    } catch (error) {
      console.error('Erro ao cancelar agendamento:', error);
      alert(`Erro ao cancelar agendamento: ${error.message}`);
    }
  };

  // Adicionar esta função para verificar mensagens agendadas
  const checkForScheduledMessages = async () => {
    try {
      console.log("Checking for scheduled messages");
      // Use our loadMessages function
      const messages = await loadMessages("scheduled", 1);
      setHasScheduledMessages(messages?.totalCount > 0);
      console.log(`Has scheduled messages: ${messages?.totalCount > 0 ? 'Yes' : 'No'}`);
    } catch (error) {
      console.error("Erro ao verificar mensagens agendadas:", error);
    }
  };

  // Funções para manipulação de seleção de mensagens
  const handleSelectMessage = (messageId, checked) => {
    if (checked) {
      setSelectedMessages(prev => [...prev, messageId]);
    } else {
      setSelectedMessages(prev => prev.filter(id => id !== messageId));
      setSelectAll(false);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const pendingMessages = messagesData?.campaignMessages?.edges
        ?.filter(({ node }) => node.status === 'pending')
        ?.map(({ node }) => node.id) || [];
      setSelectedMessages(pendingMessages);
    } else {
      setSelectedMessages([]);
    }
  };

  // Função para exclusão individual de mensagem
  const handleDeleteMessage = async (messageId) => {
    try {
      const { data } = await deleteCampaignMessage({
        variables: { id: messageId }
      });

      if (data.deleteCampaignMessage.errors && data.deleteCampaignMessage.errors.length > 0) {
        message.error(data.deleteCampaignMessage.errors.join('\n'));
        return;
      }

      message.success('Mensagem excluída com sucesso!');
      
      // Remove da seleção se estava selecionada
      setSelectedMessages(prev => prev.filter(id => id !== messageId));
      
      // Recarrega as mensagens e a campanha para atualizar o contador
      await refetchMessages();
      await refetchCampaign();
    } catch (error) {
      console.error('Erro ao excluir mensagem:', error);
      message.error(`Erro ao excluir mensagem: ${error.message}`);
    }
  };

  // Função para exclusão em lote
  const handleDeleteSelectedMessages = async () => {
    if (selectedMessages.length === 0) {
      message.warning('Nenhuma mensagem selecionada para exclusão.');
      return;
    }

    try {
      const { data } = await deleteSelectedCampaignMessages({
        variables: { ids: selectedMessages }
      });

      if (data.deleteSelectedCampaignMessages.errors && data.deleteSelectedCampaignMessages.errors.length > 0) {
        message.error(data.deleteSelectedCampaignMessages.errors.join('\n'));
        return;
      }

      message.success(`${data.deleteSelectedCampaignMessages.deletedCount} mensagens excluídas com sucesso!`);
      
      // Limpa a seleção
      setSelectedMessages([]);
      setSelectAll(false);
      
      // Recarrega as mensagens e a campanha para atualizar o contador
      await refetchMessages();
      await refetchCampaign();
    } catch (error) {
      console.error('Erro ao excluir mensagens:', error);
      message.error(`Erro ao excluir mensagens: ${error.message}`);
    }
  };

  // Limpar seleções quando o filtro mudar
  useEffect(() => {
    setSelectedMessages([]);
    setSelectAll(false);
  }, [messageStatus]);

  if (campaignLoading) return <Spin size="large" />;
  if (campaignError) return <Alert type="error" message={`Erro ao carregar campanha: ${campaignError.message}`} />;

  const campaign = campaignData?.campaign;

  if (!campaign) return <Alert type="error" message="Campanha não encontrada" />;

  // Calcular mensagens pendentes para o checkbox do cabeçalho
  const pendingMessages = messagesData?.campaignMessages?.edges
    ?.filter(({ node }) => node.status === 'pending') || [];
  const allPendingSelected = pendingMessages.length > 0 && 
    pendingMessages.every(({ node }) => selectedMessages.includes(node.id));

  const messageColumns = [
    // Coluna de checkbox - apenas para mensagens pendentes
    {
      title: (
        <Checkbox
          checked={allPendingSelected}
          indeterminate={selectedMessages.length > 0 && !allPendingSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
          disabled={pendingMessages.length === 0}
        />
      ),
      key: 'select',
      width: '50px',
      render: (_, record) => {
        if (record.status === 'pending') {
          return (
            <Checkbox
              checked={selectedMessages.includes(record.id)}
              onChange={(e) => handleSelectMessage(record.id, e.target.checked)}
            />
          );
        }
        return null;
      },
    },
    {
      title: 'Cliente',
      dataIndex: ['customer', 'name'],
      key: 'customerName',
    },
    {
      title: 'WhatsApp',
      dataIndex: ['customer', 'whatsapp'],
      key: 'whatsapp',
    },
    {
      title: 'Mensagem',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      width: '25%',
      render: (text, record) => (
        <Space>
          <Tooltip title="Visualizar mensagem completa">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewMessage(record)}
              size="small"
            />
          </Tooltip>
          {text && text.length > 50 ? `${text.substring(0, 50)}...` : text}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Tag color={getStatusColor(status)}>
          {getStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: 'Agendado para',
      dataIndex: 'scheduledFor',
      key: 'scheduledFor',
      render: (date) => date ? format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : '-',
    },
    {
      title: 'Erro',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      ellipsis: true,
      render: (text) => text || '-',
    },
    {
      title: 'Ações',
      key: 'actions',
      width: '10%',
      render: (_, record) => {
        const actions = [];
        
        // Botão de cancelar para mensagens agendadas
        if (record.status === 'scheduled') {
          actions.push(
            <Popconfirm
              key="cancel"
              title="Cancelar agendamento"
              description="Tem certeza que deseja cancelar o agendamento desta mensagem?"
              onConfirm={() => handleCancelSchedule(record.id)}
              okText="Sim"
              cancelText="Não"
            >
              <Button 
                type="link" 
                danger 
                icon={<StopOutlined />} 
                loading={canceling}
                size="small"
              >
                Cancelar
              </Button>
            </Popconfirm>
          );
        }
        
        // Botão de excluir para mensagens pendentes
        if (record.status === 'pending') {
          actions.push(
            <Popconfirm
              key="delete"
              title="Excluir mensagem"
              description="Tem certeza que deseja excluir esta mensagem? Esta ação é irreversível."
              onConfirm={() => handleDeleteMessage(record.id)}
              okText="Sim"
              cancelText="Não"
            >
              <Button 
                type="link" 
                danger 
                icon={<DeleteOutlined />} 
                loading={deleting}
                size="small"
              >
                Excluir
              </Button>
            </Popconfirm>
          );
        }
        
        return actions.length > 0 ? <Space>{actions}</Space> : null;
      },
    },
  ];

  // Define tab items
  const tabItems = [
    {
      key: '1',
      label: 'Mensagens',
      children: (
        <>
          <div style={{ marginBottom: 16 }}>
            <Space wrap>
              <Button
                type={messageStatus === "" ? "primary" : "default"}
                onClick={() => handleFilterMessages("")}
              >
                Todos
              </Button>
              <Button
                type={messageStatus === "pending" ? "primary" : "default"}
                onClick={() => handleFilterMessages("pending")}
              >
                Pendentes
              </Button>
              <Button
                type={messageStatus === "scheduled" ? "primary" : "default"}
                onClick={() => handleFilterMessages("scheduled")}
              >
                Agendados
              </Button>
              <Button
                type={messageStatus === "sent" ? "primary" : "default"}
                onClick={() => handleFilterMessages("sent")}
              >
                Enviados
              </Button>
              <Button
                type={messageStatus === "failed" ? "primary" : "default"}
                onClick={() => handleFilterMessages("failed")}
              >
                Falhas
              </Button>
              
              {/* Botão de exclusão em lote - apenas quando há mensagens selecionadas */}
              {selectedMessages.length > 0 && (
                <Popconfirm
                  title="Excluir mensagens selecionadas"
                  description={`Tem certeza que deseja excluir ${selectedMessages.length} mensagem(ns) selecionada(s)? Esta ação é irreversível.`}
                  onConfirm={handleDeleteSelectedMessages}
                  okText="Sim"
                  cancelText="Não"
                >
                  <Button 
                    type="primary" 
                    danger 
                    icon={<DeleteOutlined />}
                    loading={deletingSelected}
                  >
                    Apagar mensagens selecionadas ({selectedMessages.length})
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </div>

          {/* Campo de pesquisa */}
          <div style={{ marginBottom: 16 }}>
            <Row gutter={8}>
              <Col flex="auto">
                <Input
                  placeholder="Pesquisar por nome do cliente ou WhatsApp..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  prefix={<SearchOutlined />}
                />
              </Col>
              <Col>
                <Button 
                  type="primary" 
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  Pesquisar
                </Button>
              </Col>
              {(searchTerm || searchInput) && (
                <Col>
                  <Button 
                    icon={<ClearOutlined />}
                    onClick={handleClearSearch}
                  >
                    Limpar
                  </Button>
                </Col>
              )}
            </Row>
            {searchTerm && (
              <div style={{ marginTop: 8 }}>
                <Tag color="blue">
                  Pesquisando por: "{searchTerm}"
                </Tag>
              </div>
            )}
          </div>

          <Spin spinning={messagesLoading}>
            <Table
              columns={messageColumns}
              dataSource={messagesData?.campaignMessages?.edges.map(({ node }) => ({
                ...node,
                key: node.id
              })) || []}
              pagination={{
                current: messagesPage,
                pageSize: messagesPerPage,
                total: messagesData?.campaignMessages?.totalCount || 0,
                onChange: handlePageChange
              }}
            />
          </Spin>
        </>
      )
    }
  ];

  // Add Products tab if this is an offer campaign
  if (campaign.kind === 'offer') {
    tabItems.push({
      key: '2',
      label: 'Produtos',
      children: (
        <Card>
          <Title level={5}>
            {campaign.campaignMode === 'group' ? (
              <Space>
                <TeamOutlined style={{ color: '#1890ff' }} />
                Grupo de Produtos
              </Space>
            ) : (
              <Space>
                <ShoppingCartOutlined style={{ color: '#ff4d4f' }} />
                Produtos Selecionados
              </Space>
            )}
          </Title>
          
          {campaign.campaignMode === 'group' || campaign?.group?.id ? (
            <div style={{ marginTop: 16 }}>
              <Statistic 
                title="Grupo" 
                value={campaign.group.name || 'N/A'} 
                prefix={<TeamOutlined />}
              />
              <Alert
                style={{ marginTop: 16 }}
                message="Grupo de Produtos"
                description="Esta campanha de oferta foi criada a partir de um grupo de produtos."
                type="info"
                showIcon
              />
            </div>
          ) : (
            <div style={{ marginTop: 16 }}>
              {campaign.selectedProducts && campaign.selectedProducts.length > 0 ? (
                <List
                  bordered
                  dataSource={campaign.selectedProducts}
                  renderItem={product => (
                    <List.Item>
                      <Space>
                        <ShoppingCartOutlined />
                        <span>{product.name}</span>
                        {product.price && (
                          <Tag color="green">R$ {Number(product.price).toFixed(2)}</Tag>
                        )}
                      </Space>
                    </List.Item>
                  )}
                />
              ) : (
                <Alert
                  message="Nenhum produto encontrado"
                  description="Esta campanha não possui produtos selecionados ou os dados não estão disponíveis."
                  type="warning"
                  showIcon
                />
              )}
            </div>
          )}
        </Card>
      )
    });
  }

  const renderCampaignInfo = () => {
    const campaign = campaignData?.campaign;
    
    if (!campaign) return null;
    
    return (
      <>
        <Card title="Informações da Campanha" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic 
                title="Tipo" 
                value={getKindLabel(campaign.kind)} 
                prefix={getKindIcon(campaign.kind)} 
              />
            </Col>
            {campaign.discountPercentage > 0 && (
              <Col span={6}>
                <Statistic 
                  title="Desconto" 
                  value={`${campaign.discountPercentage}%`} 
                  prefix={<PercentageOutlined style={{ color: '#389e0d' }} />} 
                />
              </Col>
            )}
            <Col span={6}>
              <Statistic 
                title="Mensagens" 
                value={campaign.campaignMessagesCount} 
                prefix={<WhatsAppOutlined style={{ color: '#1890ff' }} />} 
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="WhatsApp Validado" 
                value={campaign.onlyVerifiedWhatsappCampaigns ? 'Apenas Validados' : 'Todos'} 
                prefix={<WhatsAppOutlined style={{ color: campaign.onlyVerifiedWhatsappCampaigns ? '#52c41a' : '#1890ff' }} />} 
              />
            </Col>
          </Row>
          
          {campaign.images && campaign.images.length > 0 && (
            <>
              <Divider>Imagens da Campanha</Divider>
              <Row gutter={16}>
                {campaign.images.map(image => (
                  <Col key={image.id} span={6} style={{ marginBottom: 16 }}>
                    <img 
                      src={image.url} 
                      alt="Imagem da campanha" 
                      style={{ 
                        width: '100%', 
                        height: 150, 
                        objectFit: 'cover',
                        cursor: 'pointer',
                        borderRadius: 4
                      }}
                      onClick={() => {
                        Modal.info({
                          title: 'Visualização da Imagem',
                          content: (
                            <div style={{ textAlign: 'center', marginTop: 16 }}>
                              <img 
                                src={image.url} 
                                alt="Imagem da campanha" 
                                style={{ maxWidth: '100%', maxHeight: '70vh' }}
                              />
                            </div>
                          ),
                          width: 'auto',
                          maskClosable: true
                        });
                      }}
                    />
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Card>
      </>
    );
  };

  return (
    <div>
      <Card>
        <Row gutter={[16, 16]}>
          <Col span={18}>
            <Title level={4}>
              <Space>
                <WhatsAppOutlined />
                {campaign.name}
              </Space>
            </Title>
          </Col>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Row gutter={[0, 8]}>
              <Col span={24}>
                <Button
                  type="primary"
                  icon={<ClockCircleOutlined />}
                  onClick={handleReschedule}
                  loading={scheduling}
                  style={{ width: '100%' }}
                >
                  Reagendar Envio
                </Button>
              </Col>
              <Col span={24}>
                <Popconfirm
                  title="Cancelar agendamentos"
                  description="Tem certeza que deseja cancelar o agendamento de TODAS as mensagens?"
                  onConfirm={handleCancelAllSchedules}
                  okText="Sim"
                  cancelText="Não"
                  disabled={!hasScheduledMessages}
                >
                  <Button
                    type="primary"
                    danger
                    icon={<StopOutlined />}
                    loading={cancelingAll}
                    style={{ width: '100%' }}
                    disabled={!hasScheduledMessages}
                  >
                    Cancelar Agendamentos
                  </Button>
                </Popconfirm>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={8}>
            <Statistic
              title="Criada em"
              value={format(new Date(campaign.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
            />
          </Col>
        </Row>

        {renderCampaignInfo()}
      </Card>

      <Divider />

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />

      {/* Adicionar Modal de reagendamento */}
      <Modal
        title="Reagendar Mensagens"
        open={rescheduleModalVisible}
        onCancel={() => {
          setRescheduleModalVisible(false);
          setScheduledDate(null);
        }}
        onOk={handleConfirmReschedule}
        okText="Reagendar"
        cancelText="Cancelar"
      >
        <p>Selecione a data e hora para reagendar as mensagens pendentes:</p>
        <DatePicker
          showTime
          format="DD/MM/YYYY HH:mm"
          placeholder="Selecione a data e hora"
          onChange={setScheduledDate}
          style={{ width: '100%' }}
          locale={locale}
          disabledDate={disabledDate}
          disabledTime={disabledTime}
          showNow={isCurrentValid}
        />
        <p style={{ marginTop: 8 }}>
          <small>* Se nenhuma data for selecionada, as mensagens serão enviadas imediatamente.</small>
        </p>
      </Modal>

      {/* Use the shared campaign message modal component */}
      <CampaignMessageModal
        message={currentMessage}
        visible={messageModalVisible}
        onClose={handleCloseMessageModal}
        onMessageUpdated={(updatedMessage) => {
          // Refetch messages to get updated data from server
          refetchMessages();
        }}
      />
    </div>
  );
};

export default CampaignDetailsModal;